import { ModalTitle, ModalClose, ModalDescription } from '@/components/modal/Modal'
import StateContent from './StateContent/StateContent'
import { JURISDICTION_TABS } from '../constants'
import FederalContent from './FederalContent/FederalContent'
import { XClose } from '@/assets/icons'
import { selectNumberOfCurrentSourcesShownInFederalTab, selectResearchFiltersDisplayForStates } from '../store/selectors'
import { useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { StatesFilterOptions } from './StateContent/StatesFilterOptions'
import { StateSelectionProps } from '../types'

type MobileDialogOptionsProps = {
  activeTab: string
  handleTabChange: (tab: string) => void
  conversationId: string
  showWarningBanner: boolean
  stateSelectionProps: StateSelectionProps
  onOpenChange: (open: boolean) => void
}

export default function MobileDialogOptions(props: MobileDialogOptionsProps) {
  const { activeTab, handleTabChange, conversationId, stateSelectionProps, showWarningBanner, onOpenChange } = props

  const handleKeyDown = (e: React.KeyboardEvent, tab: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleTabChange(tab)
    }
  }

  // Get description from filters UI state (reflects current selections in dialog)
  const displayResearchSourcesDescription = useAppSelector((state: RootState) => selectResearchFiltersDisplayForStates(state, { conversationId }))

  const numberOfCurrentSourcesShownInFederalTab = useAppSelector((state: RootState) => selectNumberOfCurrentSourcesShownInFederalTab(state, { conversationId }))

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Header */}
      <div className="sticky top-0 bg-brand-neutral-50 z-10 rounded-t-2xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-brand-neutral-200">
          <ModalTitle className="flex-1 text-brand-neutral-950 text-lg font-semibold text-center font-rethink-sans">Research Sources</ModalTitle>
          <ModalClose asChild>
            <button
              className="w-7 h-7 rounded-full flex items-center justify-center text-brand-neutral-600 hover:bg-brand-neutral-200 focus:outline-hidden transition-all duration-300"
              aria-label="Close"
              type="button"
              onClick={() => onOpenChange(false)}
            >
              <XClose className="size-6" />
            </button>
          </ModalClose>
        </div>
        <ul className="flex justify-between h-[52px] w-full" role="tablist" aria-label="Research source types">
          <li
            id="states-tab"
            role="tab"
            aria-selected={activeTab === JURISDICTION_TABS.STATES}
            aria-controls="states-tabpanel"
            tabIndex={activeTab === JURISDICTION_TABS.STATES ? 0 : -1}
            className={`flex flex-col font-semibold text-sm font-rethink-sans hover:bg-brand-neutral-100 min-w-1/2 max-w-1/2 py-3 px-3 justify-center w-1/2 text-center border-b ${
              activeTab === JURISDICTION_TABS.STATES
                ? 'text-brand-500 border-b-2 border-brand-500 bg-brand-neutral-100'
                : 'text-brand-neutral-800 border-brand-neutral-200'
            }`}
            onClick={() => handleTabChange(JURISDICTION_TABS.STATES)}
            onKeyDown={(e) => handleKeyDown(e, JURISDICTION_TABS.STATES)}
          >
            <span>States</span>
            {displayResearchSourcesDescription !== '' && (
              <span className="text-brand-neutral-500 font-normal text-xs">{displayResearchSourcesDescription}</span>
            )}
          </li>
          <li
            id="federal-tab"
            role="tab"
            aria-selected={activeTab === JURISDICTION_TABS.FEDERAL}
            aria-controls="federal-tabpanel"
            tabIndex={activeTab === JURISDICTION_TABS.FEDERAL ? 0 : -1}
            className={`flex flex-col font-semibold text-sm  font-rethink-sans min-w-1/2 max-w-1/2 py-3 px-3 justify-center hover:bg-brand-neutral-100 w-1/2 text-center border-b ${
              activeTab === JURISDICTION_TABS.FEDERAL
                ? 'text-brand-500 border-b-2 border-brand-500 bg-brand-neutral-100'
                : 'text-brand-neutral-800 border-brand-neutral-200'
            }`}
            onClick={() => handleTabChange(JURISDICTION_TABS.FEDERAL)}
            onKeyDown={(e) => handleKeyDown(e, JURISDICTION_TABS.FEDERAL)}
          >
            <span>Federal</span>
            {numberOfCurrentSourcesShownInFederalTab > 0 && (
              <span className="text-brand-neutral-500 font-normal text-xs">
                {numberOfCurrentSourcesShownInFederalTab}
                {numberOfCurrentSourcesShownInFederalTab > 1 ? ' selections' : ' selection'}
              </span>
            )}
          </li>
        </ul>

        {/* Description */}
        <ModalDescription className="text-brand-neutral-600 text-sm font-normal py-4 px-6 border-b border-brand-neutral-200">
          Select at least one source to enable case law, statutory, and regulatory research.
        </ModalDescription>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto overscroll-contain">
        {/* Conditional rendering content based on active tab */}
        {activeTab === JURISDICTION_TABS.STATES && (
          <div id="states-tabpanel" role="tabpanel" aria-labelledby="states-tab">
            <StateContent conversationId={conversationId} stateSelectionProps={stateSelectionProps} />
            <StatesFilterOptions
              conversationId={conversationId}
              hasSelectedStates={stateSelectionProps.selectedStates.length > 0}
              includeRelatedFederalContent={stateSelectionProps.includeRelatedFederalContent}
              setShowWarningBanner={stateSelectionProps.setShowWarningBanner}
              includeUnpublishedCases={stateSelectionProps.includeUnpublishedCases}
              showWarningBanner={showWarningBanner}
            />
          </div>
        )}
        {activeTab === JURISDICTION_TABS.FEDERAL && (
          <div id="federal-tabpanel" role="tabpanel" aria-labelledby="federal-tab">
            <FederalContent conversationId={conversationId} />
          </div>
        )}
      </div>
    </div>
  )
}
